import { useLocalStorage } from "@vueuse/core";
import { computed, toValue, watch } from "vue";
import { OVERLAY_LOCALES } from "@/core/constants/language-and-currency";
import { useLanguages } from "./useLanguages";
import { useThemeContext } from "./useThemeContext";
import type { ILanguage } from "@/core/types/language";
import type { IPage } from "@/shared/thorlabs/shared";
import type { ComputedRef, MaybeRefOrGetter } from "vue";

/** The subset of a resolved slug's entity info the overlay fallback decision needs. */
export interface IOverlayFallbackEntityInfo {
  objectId?: string | null;
  languageCode?: string | null;
}

/** Reactive inputs the matcher supplies to install an overlay fallback redirect. */
export interface IOverlayFallbackRedirectParams {
  loading: MaybeRefOrGetter<boolean>;
  isEditable: MaybeRefOrGetter<boolean>;
  isPreview: MaybeRefOrGetter<boolean>;
  entityInfo: MaybeRefOrGetter<IOverlayFallbackEntityInfo | null | undefined>;
  seoUrl: MaybeRefOrGetter<string>;
}

/** Public surface of a content-overlay locale engine (one per entry in `useOverlayLocales`). */
export interface IContentOverlayLocale {
  locale: string;
  overlayLanguage: ComputedRef<ILanguage | undefined>;
  hasTranslation: (page: IPage) => boolean;
  saveLanguageChange: (locale: string) => void;
  userHasChangedLanguage: () => boolean;
  shouldAutoSelect: ComputedRef<boolean>;
  contentCulture: ComputedRef<string | undefined>;
  isActiveLanguage: (pageHasOverlay: MaybeRefOrGetter<boolean | undefined>) => ComputedRef<boolean>;
  showOption: (pageHasOverlay: MaybeRefOrGetter<boolean | undefined>) => ComputedRef<boolean>;
  setupFallbackRedirect: (params: IOverlayFallbackRedirectParams) => {
    fallbackRedirectTarget: ComputedRef<string | null>;
  };
}

/**
 * Configuration for a single content-overlay locale. Captures the only three things that differ
 * between overlay locales (German, French, …); all behavior is shared by `useContentOverlayLocale`.
 */
export interface IContentOverlayLocaleConfig {
  /** Two-letter overlay locale code, e.g. "de" or "fr". */
  locale: string;
  /** localStorage key holding the 30-day manual-switch suppression timestamp for this locale. */
  timestampStorageKey: string;
  /** Geolocation predicate deciding whether this overlay should auto-select for the visitor. */
  autoSelectWhen: () => boolean;
}

/**
 * Pure decision: should the app leave the `/{locale}/` URL because the resolved page has no variant
 * for that overlay locale? Returns the English path to redirect to, or `null` to stay.
 *
 * An overlay locale is content served via `cultureName`, not a real locale. When the user is on a
 * `/{locale}/` URL but the resolved SEO record isn't that locale — i.e. the target page has no such
 * version — the `/{locale}/` prefix is dropped and navigation continues on the English URL.
 *
 * Extracted as a plain function so the decision can be unit-tested without a DOM or a router.
 */
export function resolveOverlayFallbackTarget(params: {
  localeCode: string;
  loading: boolean;
  isEditable: boolean;
  isPreview: boolean;
  currentLocale: string;
  entityInfo: IOverlayFallbackEntityInfo | null | undefined;
  seoUrl: string;
}): string | null {
  const { localeCode, loading, isEditable, isPreview, currentLocale, entityInfo, seoUrl } = params;

  if (loading || isEditable || isPreview || currentLocale !== localeCode) {
    return null;
  }
  // Act only once the slug resolved to a real entity.
  if (!entityInfo?.objectId) {
    return null;
  }
  // Stay on /{locale}/ only for a genuine record of that locale. Everything else — an English
  // record (page has no variant) or a language-neutral entity such as a CatalogProduct
  // (SKU-based /item/{code}, no localized variant) — drops the /{locale}/ prefix.
  if (entityInfo.languageCode?.split("-")[0].toLowerCase() === localeCode) {
    return null;
  }
  return seoUrl === "" ? "/" : `/${seoUrl}`;
}

/**
 * Shared engine for a content-overlay locale.
 *
 * An overlay locale is not a catalog locale: the app-wide culture stays English while the overlay's
 * *content* is fetched by passing its `cultureName` and the URL carries a `/{locale}/` prefix. This
 * engine owns the questions that drive that overlay — "should it auto-select?", "is it the active
 * language right now?", "must we fall back off /{locale}/?" — so callers never re-derive them.
 *
 * The `useOverlayLocales` registry instantiates one configured engine per overlay locale.
 */
export function useContentOverlayLocale(config: IContentOverlayLocaleConfig): IContentOverlayLocale {
  const localeStatus = useLocalStorage<number>(config.timestampStorageKey, 0);

  const { currentLocale, savedCartLanguage, supportedLocales } = useLanguages();
  const { themeContext } = useThemeContext();

  const hasExplicitLanguagePrefix = computed(() => {
    const localeInPath = location.pathname.split("/")[1];
    return supportedLocales.value.includes(localeInPath) || OVERLAY_LOCALES.includes(localeInPath);
  });

  const overlayLanguage = computed<ILanguage | undefined>(() =>
    themeContext.value.availableLanguages.find((language) => language.twoLetterLanguageName === config.locale),
  );

  function saveLanguageChange(locale: string) {
    if (locale === config.locale) {
      localeStatus.value = 0;
    } else {
      localeStatus.value = Date.now();
    }
  }

  function hasTranslation(page: IPage) {
    return page.existingLanguages?.some((lang) => lang.name === config.locale) ?? false;
  }

  function userHasChangedLanguage() {
    const timestamp = localeStatus.value;
    if (!timestamp) {
      return false;
    }

    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    const thirtyDaysAgo = Date.now() - thirtyDaysInMs;

    return timestamp >= thirtyDaysAgo;
  }

  // A geolocated visitor gets this overlay auto-selected, unless they manually switched language
  // within the last 30 days (tracked via the per-locale timestamp). Independent of whether the
  // current page actually has a variant — that is gated separately by the page flag at each call
  // site.
  const shouldAutoSelect = computed(
    () =>
      !hasExplicitLanguagePrefix.value &&
      config.autoSelectWhen() &&
      !!overlayLanguage.value &&
      !userHasChangedLanguage(),
  );

  // The content culture to request for the page. Overlay content is served by passing the overlay
  // `cultureName` to the slug/content query WITHOUT switching the app-wide culture. `undefined`
  // means "use the global (English) culture". Drives the matcher's content fetch.
  const contentCulture = computed<string | undefined>(() => {
    const wantsOverlay = currentLocale.value === config.locale || shouldAutoSelect.value;
    return wantsOverlay ? overlayLanguage.value?.cultureName : undefined;
  });

  // Whether this overlay is the *active* language for the selector. An explicit /{locale}/ URL
  // always wins; otherwise auto-select only applies when the current page actually offers it.
  function isActiveLanguage(pageHasOverlay: MaybeRefOrGetter<boolean | undefined>) {
    return computed(
      () =>
        !!overlayLanguage.value &&
        (currentLocale.value === config.locale || (!!toValue(pageHasOverlay) && shouldAutoSelect.value)),
    );
  }

  // Whether to list this overlay as an option: when the page offers it, or the user is already on a
  // /{locale}/ URL (so the active item is always present in the list).
  function showOption(pageHasOverlay: MaybeRefOrGetter<boolean | undefined>) {
    return computed(
      () => !!overlayLanguage.value && (!!toValue(pageHasOverlay) || currentLocale.value === config.locale),
    );
  }

  // Fallback redirect: installs a watcher that drops the user off /{locale}/ when the resolved page
  // has no variant for this overlay. Returns the reactive decision target for inspection/testing.
  function setupFallbackRedirect(params: IOverlayFallbackRedirectParams) {
    const fallbackRedirectTarget = computed<string | null>(() =>
      resolveOverlayFallbackTarget({
        localeCode: config.locale,
        loading: !!toValue(params.loading),
        isEditable: !!toValue(params.isEditable),
        isPreview: !!toValue(params.isPreview),
        currentLocale: currentLocale.value,
        entityInfo: toValue(params.entityInfo),
        seoUrl: toValue(params.seoUrl),
      }),
    );

    watch(
      fallbackRedirectTarget,
      (target) => {
        if (!target) {
          return;
        }
        // An overlay locale is a content overlay, not a cart language. A stray overlay cart language
        // (cartLocale) — e.g. persisted by an older session — would make the router's cart-language
        // restore force the cart back to /{locale}/cart. Clear it before leaving.
        if (savedCartLanguage.value === config.locale) {
          savedCartLanguage.value = "";
        }
        // Hard navigation required: the SPA router base is "/{locale}/", so router.replace cannot
        // escape the locale prefix. A full reload re-initializes the app under the English base.
        //
        // Use location.replace (not location.href): the current /{locale}/ entry has no variant, so
        // it must be REPLACED in the history stack rather than pushed. Otherwise clicking the
        // browser Back button returns to this entry, which instantly re-fires this redirect —
        // trapping the user and pushing them back to English instead of the previous page.
        window.location.replace(`${target}${window.location.search}${window.location.hash}`);
      },
      { immediate: true },
    );

    return { fallbackRedirectTarget };
  }

  return {
    locale: config.locale,
    overlayLanguage,
    hasTranslation,
    saveLanguageChange,
    userHasChangedLanguage,
    shouldAutoSelect,
    contentCulture,
    isActiveLanguage,
    showOption,
    setupFallbackRedirect,
  };
}
